import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import * as FiIcons from 'react-icons/fi';
import { DeployedApp, resolveHref } from '../../config/deployedApps';

const FiExternalLink = FiIcons.FiExternalLink as React.ComponentType<any>;
const FiArrowRight = FiIcons.FiArrowRight as React.ComponentType<any>;

interface Props {
  app: DeployedApp;
  /** Launcher variant is a touch more compact (hides the long description). */
  compact?: boolean;
}

/**
 * Shared, self-contained product/app card. Cover uses the app's own gradient +
 * inline SVG art; the CTA links to the deployed app (in-SPA Link for internal
 * routes, new-tab anchor for sibling apps on other ports).
 */
const AppCard: React.FC<Props> = ({ app, compact }) => {
  return (
    <Card>
      <Cover $from={app.accent} $to={app.accentDark}>
        <Blob />
        <app.Art />
      </Cover>
      <Body>
        <Name>{app.name}</Name>
        <Tagline>{app.tagline}</Tagline>
        {!compact && <Description>{app.description}</Description>}
        <Features>
          {app.features.map((f) => (
            <Chip key={f} $accent={app.accent}>{f}</Chip>
          ))}
        </Features>
        {app.link.external ? (
          <LaunchA
            href={resolveHref(app.link)}
            target="_blank"
            rel="noopener noreferrer"
            $accent={app.accent}
            $accentDark={app.accentDark}
          >
            Open {app.name} <FiExternalLink />
          </LaunchA>
        ) : (
          <LaunchLink to={app.link.path} $accent={app.accent} $accentDark={app.accentDark}>
            Open {app.name} <FiArrowRight />
          </LaunchLink>
        )}
      </Body>
    </Card>
  );
};

export default AppCard;

// ── styled ────────────────────────────────────────────────────────────────────

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #E7ECEC;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(13,52,47,.05);
  transition: transform .16s ease, box-shadow .16s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 34px rgba(13,52,47,.14);
  }
`;

interface CoverProps { $from: string; $to: string; }
const Cover = styled.div<CoverProps>`
  position: relative;
  height: 128px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${p => `linear-gradient(135deg, ${p.$from} 0%, ${p.$to} 100%)`};

  svg { position: relative; z-index: 1; }
`;

const Blob = styled.div`
  position: absolute;
  top: -40px;
  right: -30px;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: rgba(255,255,255,.12);
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 18px 20px;
  flex-grow: 1;
`;

const Name = styled.h3`
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -.01em;
  color: #14222B;
  margin: 0;
`;

const Tagline = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: #8A98A0;
`;

const Description = styled.p`
  font-size: 13.5px;
  line-height: 1.5;
  color: #5E6E72;
  margin: 2px 0 4px;
`;

const Features = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 2px 0 6px;
`;

interface ChipProps { $accent: string; }
const Chip = styled.span<ChipProps>`
  font-size: 11.5px;
  font-weight: 600;
  color: ${p => p.$accent};
  background: ${p => p.$accent}14;
  border-radius: 20px;
  padding: 4px 10px;
`;

interface LaunchProps { $accent: string; $accentDark: string; }
const launchStyles = css<LaunchProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: auto;
  padding: 11px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  color: #ffffff;
  background: ${p => `linear-gradient(135deg, ${p.$accent} 0%, ${p.$accentDark} 100%)`};
  box-shadow: 0 6px 15px ${p => p.$accent}40;
  transition: transform .12s ease, filter .16s ease;

  &:hover { filter: brightness(1.06); }
  &:active { transform: scale(.98); }
  svg { font-size: 16px; }
`;
const LaunchA = styled.a<LaunchProps>`${launchStyles}`;
const LaunchLink = styled(Link)<LaunchProps>`${launchStyles}`;
